/* File: loading.jsx
 * Path: ananunadm/src/helpers/loading.jsx
*/
import { memo } from "react";
import { Box, useMantineColorScheme } from "@mantine/core";

const Loading = memo(function Loading({ progress, text = "Loading", fullscreen = false }) {
	const { colorScheme } = useMantineColorScheme();

	return (
		<Box
			style={{
				position: fullscreen ? "fixed" : "relative",
				top: fullscreen ? 0 : "auto",
				left: fullscreen ? 0 : "auto",
				right: fullscreen ? 0 : "auto",
				bottom: fullscreen ? 0 : "auto",
				width: "100%",
				minHeight: fullscreen ? "100vh" : "calc(100vh - 220px)",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				zIndex: fullscreen ? 9999 : 1,
				overflow: "hidden"
			}}
		>
			{/* Radial blur layers - stronger in center, weaker at edges */}

			{/* Edge layer - blur 1px */}
			<Box
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backdropFilter: "blur(1px)",
					WebkitBackdropFilter: "blur(1px)",
					background: colorScheme === "dark" ? "rgba(10, 10, 15, 0.1)" : "rgba(255, 255, 255, 0.1)"
				}}
			/>

			{/* Layer 2 - blur 3px */}
			<Box
				style={{
					position: "absolute",
					top: "5%",
					left: "5%",
					right: "5%",
					bottom: "5%",
					borderRadius: "50%",
					backdropFilter: "blur(3px)",
					WebkitBackdropFilter: "blur(3px)",
					background: colorScheme === "dark" ? "rgba(10, 10, 15, 0.1)" : "rgba(255, 255, 255, 0.1)",
					animation: "pulseLayer 4s ease-in-out infinite"
				}}
			/>

			{/* Layer 3 - blur 5px */}
			<Box
				style={{
					position: "absolute",
					top: "12%",
					left: "12%",
					right: "12%",
					bottom: "12%",
					borderRadius: "50%",
					backdropFilter: "blur(5px)",
					WebkitBackdropFilter: "blur(5px)",
					background: colorScheme === "dark" ? "rgba(10, 10, 15, 0.1)" : "rgba(255, 255, 255, 0.1)",
					animation: "pulseLayer 4s ease-in-out infinite 0.2s"
				}}
			/>

			{/* Layer 4 - blur 7px */}
			<Box
				style={{
					position: "absolute",
					top: "20%",
					left: "20%",
					right: "20%",
					bottom: "20%",
					borderRadius: "50%",
					backdropFilter: "blur(7px)",
					WebkitBackdropFilter: "blur(7px)",
					background:
						colorScheme === "dark" ? "rgba(10, 10, 15, 0.15)" : "rgba(255, 255, 255, 0.15)",
					animation: "pulseLayer 4s ease-in-out infinite 0.4s"
				}}
			/>

			{/* Layer 5 - blur 10px (center) */}
			<Box
				style={{
					position: "absolute",
					top: "30%",
					left: "30%",
					right: "30%",
					bottom: "30%",
					borderRadius: "50%",
					backdropFilter: "blur(10px)",
					WebkitBackdropFilter: "blur(10px)",
					background: colorScheme === "dark" ? "rgba(10, 10, 15, 0.2)" : "rgba(255, 255, 255, 0.2)",
					animation: "pulseLayer 4s ease-in-out infinite 0.6s"
				}}
			/>

			{/* Animated gradient orbs */}
			<Box
				style={{
					position: "absolute",
					width: "400px",
					height: "400px",
					borderRadius: "50%",
					background: "var(--app-gradient-primary)",
					filter: "blur(80px)",
					opacity: 0.3,
					top: "10%",
					left: "10%",
					animation: "float1 8s ease-in-out infinite",
					pointerEvents: "none"
				}}
			/>
			<Box
				style={{
					position: "absolute",
					width: "350px",
					height: "350px",
					borderRadius: "50%",
					background: "var(--app-gradient-warm)",
					filter: "blur(80px)",
					opacity: 0.25,
					bottom: "10%",
					right: "10%",
					animation: "float2 10s ease-in-out infinite",
					pointerEvents: "none"
				}}
			/>
			<Box
				style={{
					position: "absolute",
					width: "300px",
					height: "300px",
					borderRadius: "50%",
					background: "var(--app-gradient-cool)",
					filter: "blur(60px)",
					opacity: 0.2,
					top: "50%",
					right: "20%",
					transform: "translateY(-50%)",
					animation: "float3 7s ease-in-out infinite",
					pointerEvents: "none"
				}}
			/>

			{/* Main content container */}
			<Box
				style={{
					position: "relative",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					zIndex: 10
				}}
			>
				{/* APIT Logo - Big */}
				<Box
					style={{
						position: "relative",
						width: "250px",
						height: "250px",
						marginBottom: "40px"
					}}
				>
					{/* Glow effect behind logo */}
					<Box
						style={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: "200px",
							height: "200px",
							borderRadius: "50%",
							background: "var(--app-gradient-primary)",
							filter: "blur(50px)",
							opacity: 0.5,
							animation: "glowPulse 2.5s ease-in-out infinite"
						}}
					/>

					{/* Spinning outer ring */}
					<Box
						style={{
							position: "absolute",
							top: "-10px",
							left: "-10px",
							width: "270px",
							height: "270px",
							borderRadius: "50%",
							border: "3px solid transparent",
							borderTopColor: "rgba(59, 130, 246, 0.6)",
							borderRightColor: "rgba(139, 92, 246, 0.4)",
							animation: "spin 3s linear infinite"
						}}
					/>

					{/* Spinning inner ring */}
					<Box
						style={{
							position: "absolute",
							top: "10px",
							left: "10px",
							width: "230px",
							height: "230px",
							borderRadius: "50%",
							border: "2px solid transparent",
							borderBottomColor: "rgba(236, 72, 153, 0.6)",
							borderLeftColor: "rgba(245, 158, 11, 0.4)",
							animation: "spinReverse 2.5s linear infinite"
						}}
					/>

					{/* Pulsing ring */}
					<Box
						style={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: "180px",
							height: "180px",
							borderRadius: "50%",
							border: "2px solid rgba(59, 130, 246, 0.3)",
							animation: "ringPulse 2s ease-out infinite"
						}}
					/>
					<Box
						style={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: "180px",
							height: "180px",
							borderRadius: "50%",
							border: "2px solid rgba(139, 92, 246, 0.3)",
							animation: "ringPulse 2s ease-out infinite 0.5s"
						}}
					/>
					<Box
						style={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: "180px",
							height: "180px",
							borderRadius: "50%",
							border: "2px solid rgba(236, 72, 153, 0.3)",
							animation: "ringPulse 2s ease-out infinite 1s"
						}}
					/>

					{/* Logo image */}
					{/* <img
						src="/animated.svg"
						alt="APIT Logo"
						style={{
							position: "relative",
							width: "100%",
							height: "100%",
							objectFit: "contain",
							animation: "logoFloat 3s ease-in-out infinite",
							filter:
								colorScheme === "dark"
									? "drop-shadow(0 0 40px rgba(59, 130, 246, 0.6))"
									: "drop-shadow(0 0 30px rgba(59, 130, 246, 0.4))"
						}}
					/> */}
				</Box>
			</Box>

			{/* CSS Animations */}
			<style>
				{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    
                    @keyframes spinReverse {
                        0% { transform: rotate(360deg); }
                        100% { transform: rotate(0deg); }
                    }
                    
                    @keyframes logoFloat {
                        0%, 100% { 
                            transform: translateY(0) scale(1);
                        }
                        50% { 
                            transform: translateY(-10px) scale(1.02);
                        }
                    }
                    
                    @keyframes glowPulse {
                        0%, 100% { 
                            opacity: 0.4;
                            transform: translate(-50%, -50%) scale(1);
                        }
                        50% { 
                            opacity: 0.7;
                            transform: translate(-50%, -50%) scale(1.3);
                        }
                    }
                    
                    @keyframes ringPulse {
                        0% {
                            transform: translate(-50%, -50%) scale(1);
                            opacity: 0.6;
                        }
                        100% {
                            transform: translate(-50%, -50%) scale(1.8);
                            opacity: 0;
                        }
                    }
                    
                    @keyframes pulseLayer {
                        0%, 100% {
                            opacity: 1;
                            transform: scale(1);
                        }
                        50% {
                            opacity: 0.8;
                            transform: scale(1.02);
                        }
                    }
                    
                    @keyframes loadingSlide {
                        0% { 
                            transform: translateX(-100%);
                        }
                        100% { 
                            transform: translateX(400%);
                        }
                    }
                    
                    @keyframes gradientText {
                        0% {
                            background-position: 0% center;
                        }
                        100% {
                            background-position: 200% center;
                        }
                    }
                    
                    @keyframes float1 {
                        0%, 100% { 
                            transform: translate(0, 0) scale(1);
                        }
                        25% { 
                            transform: translate(50px, -30px) scale(1.1);
                        }
                        50% { 
                            transform: translate(20px, 40px) scale(0.95);
                        }
                        75% { 
                            transform: translate(-30px, 20px) scale(1.05);
                        }
                    }
                    
                    @keyframes float2 {
                        0%, 100% { 
                            transform: translate(0, 0) scale(1);
                        }
                        25% { 
                            transform: translate(-40px, 30px) scale(0.95);
                        }
                        50% { 
                            transform: translate(-20px, -50px) scale(1.1);
                        }
                        75% { 
                            transform: translate(30px, -20px) scale(1);
                        }
                    }
                    
                    @keyframes float3 {
                        0%, 100% { 
                            transform: translateY(-50%) scale(1);
                        }
                        50% { 
                            transform: translateY(-50%) translateX(-40px) scale(1.1);
                        }
                    }
                `}
			</style>
		</Box>
	);
});

export default Loading;
